import { useMutation, MutationFunction, MutationOptions } from 'react-query';
import {
    CreateFormQuestionBody,
    FormQuestionResponse,
    MoveFormQuestionBody,
    UpdateFormQuestionBody,
    createFormQuestion,
    duplicateFormQuestion,
    updateFormQuestion,
    moveFormQuestion,
    deleteFormQuestion,
} from '../../services/apiSLTP/qhse/forms/formQuestions';

// --- CREATE ---
type UseCreateFormQuestionMutationOptions = MutationOptions<FormQuestionResponse, Error, CreateFormQuestionBody>;

export const useCreateFormQuestionMutation = (options?: UseCreateFormQuestionMutationOptions) => {
    const mutationFn: MutationFunction<FormQuestionResponse, CreateFormQuestionBody> = async (data) => {
        try {
            return await createFormQuestion(data);
        } catch (error) {
            throw new Error('An unexpected error occurred during formQuestion creation');
        }
    };

    return useMutation(mutationFn, options);
};

// --- DUPLICATE ---
type UseDuplicateFormQuestionMutationOptions = MutationOptions<FormQuestionResponse, Error, { questionId: string }>;

export const useDuplicateFormQuestionMutation = (options?: UseDuplicateFormQuestionMutationOptions) => {
    const mutationFn: MutationFunction<FormQuestionResponse, { questionId: string }> = async ({ questionId }) => {
        try {
            return await duplicateFormQuestion(questionId);
        } catch (error) {
            throw new Error('An unexpected error occurred during formQuestion duplication');
        }
    };

    return useMutation(mutationFn, options);
};

// --- UPDATE ---
type UseUpdateFormQuestionMutationOptions = MutationOptions<FormQuestionResponse, Error, { questionId: string; data: UpdateFormQuestionBody }>;

export const useUpdateFormQuestionMutation = (options?: UseUpdateFormQuestionMutationOptions) => {
    const mutationFn: MutationFunction<FormQuestionResponse, { questionId: string; data: UpdateFormQuestionBody }> = async ({ questionId, data }) => {
        try {
            return await updateFormQuestion(questionId, data);
        } catch (error) {
            throw new Error('An unexpected error occurred during formQuestion update');
        }
    };

    return useMutation(mutationFn, options);
};

// --- MOVE ---
type UseMoveFormQuestionMutationOptions = MutationOptions<FormQuestionResponse, Error, { questionId: string; data: MoveFormQuestionBody }>;

export const useMoveFormQuestionMutation = (options?: UseMoveFormQuestionMutationOptions) => {
    const mutationFn: MutationFunction<FormQuestionResponse, { questionId: string; data: MoveFormQuestionBody }> = async ({ questionId, data }) => {
        try {
            return await moveFormQuestion(questionId, data);
        } catch (error) {
            throw new Error('An unexpected error occurred during formQuestion move');
        }
    };

    return useMutation(mutationFn, options);
};

// --- DELETE ---
type UseDeleteFormQuestionMutationOptions = MutationOptions<{ message: string }, Error, { questionId: string }>;

export const useDeleteFormQuestionMutation = (options?: UseDeleteFormQuestionMutationOptions) => {
    const mutationFn: MutationFunction<{ message: string }, { questionId: string }> = async ({ questionId }) => {
        try {
            return await deleteFormQuestion(questionId);
        } catch (error) {
            throw new Error('An unexpected error occurred during formQuestion deletion');
        }
    };

    return useMutation(mutationFn, options);
};
